import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  StarIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';

interface ResearchArea {
  _id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  details: string[];
  isActive: boolean;
}

interface RepresentativePaper {
  _id: string;
  title: string;
  authors: string[];
  journal: string;
  year: number;
  doi?: string;
  abstract: string;
  keywords: string[];
  coverImage?: string;
  pdfUrl?: string;
  isHighlighted: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

interface Equipment {
  _id: string;
  name: string;
  description: string;
  isActive: boolean;
}

interface ResearchContent {
  researchAreas: ResearchArea[];
  equipment: Equipment[];
}

/** 研究方向配图（按标题匹配） */
const AREA_COVER_IMAGES: Record<string, string> = {
  '钙钛矿太阳能电池': '/research-covers/research-solar-cell.jpg',
  '钙钛矿光电探测器': '/research-covers/research-photodetector.jpg',
  '钙钛矿材料合成': '/research-covers/research-materials-synthesis.jpg',
};

const Research: React.FC = () => {
  const navigate = useNavigate();
  const [researchContent, setResearchContent] = useState<ResearchContent>({
    researchAreas: [],
    equipment: []
  });
  const [representativePapers, setRepresentativePapers] = useState<RepresentativePaper[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResearchContent();
    fetchRepresentativePapers();
  }, []);

  const fetchResearchContent = async () => {
    try {
      const response = await apiService.get<ResearchContent>('/research-content');
      if (response.success && response.data) {
        setResearchContent(response.data);
      }
    } catch (error) {
      console.error('获取研究内容失败:', error);
    }
  };

  const fetchRepresentativePapers = async () => {
    try {
      const response = await apiService.get<RepresentativePaper[]>('representative-papers');
      if (response.success && response.data) {
        const sortedPapers = response.data.sort((a, b) => {
          if (a.isHighlighted !== b.isHighlighted) {
            return b.isHighlighted ? 1 : -1;
          }
          return a.order - b.order;
        });
        setRepresentativePapers(sortedPapers);
      }
    } catch (error) {
      console.error('获取代表性论文失败:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">研究方向</h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          我们专注于钙钛矿材料与器件的前沿研究，涵盖太阳能电池、发光二极管、光电探测器等多个领域，
          致力于推动新能源技术的发展。
        </p>
      </div>

      <div className="space-y-12">
        {researchContent.researchAreas.map((area) => {
          const coverImage = AREA_COVER_IMAGES[area.title];
          return (
            <div key={area._id} className="card">
              <div className="flex flex-col lg:flex-row gap-8">
                <div className="lg:w-1/3">
                  {coverImage && (
                    <div className="mb-4 overflow-hidden rounded-lg bg-gray-100">
                      <img
                        src={coverImage}
                        alt={area.title}
                        className="w-full aspect-video object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{area.title}</h2>
                  <p className="text-gray-600 mb-6">{area.description}</p>
                </div>
                <div className="lg:w-2/3">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">主要研究内容</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {area.details.map((detail, index) => (
                      <div key={index} className="flex items-center">
                        <div className="w-2 h-2 bg-primary-600 rounded-full mr-3"></div>
                        <span className="text-gray-700">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      <section className="mt-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">代表性论文</h2>
        {representativePapers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {representativePapers.map((paper) => (
              <div 
                key={paper._id} 
                className={`card hover:shadow-lg transition-shadow duration-300 cursor-pointer ${paper.isHighlighted ? 'ring-2 ring-primary-500 bg-gradient-to-br from-primary-50 to-white' : ''}`}
                onClick={() => navigate(`/representative-paper/${paper._id}`)}
              >
                {paper.coverImage && (
                  <div className="mb-4">
                    <img 
                      src={paper.coverImage} 
                      alt={paper.title}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                )}
                <div className="text-primary-600 text-sm font-semibold mb-2">{paper.journal}</div>
                <h3 className="text-lg font-bold mb-2 line-clamp-3 flex items-start">
                  {paper.title}
                  {paper.isHighlighted && (
                    <StarIcon className="h-5 w-5 text-yellow-500 ml-2 flex-shrink-0 mt-0.5" />
                  )}
                </h3>
                <div className="text-gray-600 text-sm mb-2">
                  {paper.authors.join(', ')}
                </div>
                <div className="text-gray-500 text-sm mb-3">{paper.year}</div>
                {paper.abstract && (
                  <p className="text-gray-600 text-sm mb-3 line-clamp-3">{paper.abstract}</p>
                )}
                {paper.keywords && paper.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {paper.keywords.slice(0, 3).map((keyword, index) => (
                      <span 
                        key={index} 
                        className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex justify-between items-center">
                  {paper.doi && (
                    <a 
                      href={`https://doi.org/${paper.doi}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 text-sm hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      DOI
                    </a>
                  )}
                  {paper.pdfUrl && (
                    <a 
                      href={paper.pdfUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-600 text-sm hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      查看PDF
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            <p>暂无代表性论文</p>
          </div>
        )}
      </section>

      <section className="mt-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">研究设备</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {researchContent.equipment.map((equipment) => (
            <div key={equipment._id} className="card text-center">
              <h3 className="font-semibold text-gray-900 mb-2">{equipment.name}</h3>
              <p className="text-sm text-gray-600">{equipment.description}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Research;
